import type { LibraryShelf } from "@/cloud/types/types";
import type { BoardBox, ShelfLayout } from "@/shelf/types/types";

import {
  JEWEL_CASE_HEIGHT_MILLIMETERS,
  JEWEL_CASE_THICKNESS_MILLIMETERS,
  SHELF_HEADER_HEIGHT_MILLIMETERS,
  SHELF_PANEL_THICKNESS_MILLIMETERS,
} from "@/shelf/constants/shelf-geometry";
import { resolveDrawnShelfRotation, resolveRotatedBounds } from "@/shelf/utils/shelf-rotation";

/**
 * Where every part of a rack is, from its capacity and where it stands.
 *
 * Arithmetic rather than measurement, which is the whole reason board space is
 * millimeters: a rack's width is the number of discs it holds times the
 * thickness of one, so nothing has to be read back out of the DOM to answer
 * where a slot is — at any zoom, or for a rack that is off screen.
 */
export const resolveShelfLayout = (shelf: LibraryShelf): ShelfLayout => {
  const interiorWidthMillimeters = shelf.capacity * JEWEL_CASE_THICKNESS_MILLIMETERS;
  const interiorHeightMillimeters = JEWEL_CASE_HEIGHT_MILLIMETERS;
  const outer: BoardBox = {
    xMillimeters: shelf.boardXMillimeters,
    yMillimeters: shelf.boardYMillimeters,
    widthMillimeters: interiorWidthMillimeters + 2 * SHELF_PANEL_THICKNESS_MILLIMETERS,
    heightMillimeters: interiorHeightMillimeters + 2 * SHELF_PANEL_THICKNESS_MILLIMETERS,
  };
  const rotationDegrees = resolveDrawnShelfRotation(shelf.rotationDegrees);

  // The boards as they actually stand, once the rack has been turned — about
  // its own middle, so a turned rack stays where it stood.
  const rotatedOuter = resolveRotatedBounds({
    box: outer,
    centreXMillimeters: outer.xMillimeters + outer.widthMillimeters / 2,
    centreYMillimeters: outer.yMillimeters + outer.heightMillimeters / 2,
    rotationDegrees,
  });

  // The name stands on top of the rack as it stands, outside the turned body,
  // so it never lands on the first slot of a rack lying on its side.
  const namePlate: BoardBox = {
    xMillimeters: rotatedOuter.xMillimeters,
    yMillimeters: rotatedOuter.yMillimeters - SHELF_HEADER_HEIGHT_MILLIMETERS,
    widthMillimeters: rotatedOuter.widthMillimeters,
    heightMillimeters: SHELF_HEADER_HEIGHT_MILLIMETERS,
  };

  return {
    boundingBox: {
      xMillimeters: rotatedOuter.xMillimeters,
      yMillimeters: namePlate.yMillimeters,
      widthMillimeters: rotatedOuter.widthMillimeters,
      heightMillimeters: SHELF_HEADER_HEIGHT_MILLIMETERS + rotatedOuter.heightMillimeters,
    },
    interior: {
      xMillimeters: outer.xMillimeters + SHELF_PANEL_THICKNESS_MILLIMETERS,
      yMillimeters: outer.yMillimeters + SHELF_PANEL_THICKNESS_MILLIMETERS,
      widthMillimeters: interiorWidthMillimeters,
      heightMillimeters: interiorHeightMillimeters,
    },
    namePlate,
    outer,
    rotatedOuter,
    rotationDegrees,
    shelf,
  };
};

/** Where one numbered slot is. Discs rest on the bottom board, not the top. */
export const resolveSlotBox = ({
  layout,
  slotIndex,
}: {
  layout: ShelfLayout;
  slotIndex: number;
}): BoardBox => ({
  xMillimeters: layout.interior.xMillimeters + slotIndex * JEWEL_CASE_THICKNESS_MILLIMETERS,
  yMillimeters:
    layout.interior.yMillimeters +
    layout.interior.heightMillimeters -
    JEWEL_CASE_HEIGHT_MILLIMETERS,
  widthMillimeters: JEWEL_CASE_THICKNESS_MILLIMETERS,
  heightMillimeters: JEWEL_CASE_HEIGHT_MILLIMETERS,
});

/**
 * The board's extent, which is what "fit everything on screen" is measured
 * against. Null for an empty board: there is no box around nothing, and a zero
 * one would be a point the view zoomed infinitely far into.
 */
export const resolveShelvesBoundingBox = (shelves: readonly LibraryShelf[]): BoardBox | null => {
  const boxes = shelves.map((shelf) => resolveShelfLayout(shelf).boundingBox);
  if (boxes.length === 0) {
    return null;
  }
  const left = Math.min(...boxes.map((box) => box.xMillimeters));
  const top = Math.min(...boxes.map((box) => box.yMillimeters));
  const right = Math.max(...boxes.map((box) => box.xMillimeters + box.widthMillimeters));
  const bottom = Math.max(...boxes.map((box) => box.yMillimeters + box.heightMillimeters));
  return {
    xMillimeters: left,
    yMillimeters: top,
    widthMillimeters: right - left,
    heightMillimeters: bottom - top,
  };
};
